/// VHF marine radio reference — the SRC + RYA radio-course material.
///
/// Three domains in one file:
///
///   1. Channels    — the dedicated UK / international VHF channels
///                    every skipper needs by number and purpose.
///   2. Call types  — Mayday, Pan Pan, Securité, routine — their
///                    structures, when to use each, the procedure
///                    words.
///   3. MMSI        — Maritime Mobile Service Identity — what the
///                    9-digit number is, how to read a country prefix
///                    (MID), and the special-purpose forms.
///
/// Pure data; the apps/web layer renders the UI.

// ───────────────────────────────────────────────────────────────────
// VHF channels
// ───────────────────────────────────────────────────────────────────

export type ChannelTier =
  | "distress"
  | "dsc"
  | "bridge-to-bridge"
  | "intership"
  | "port-ops"
  | "marina"
  | "coastguard-sar"
  | "weather"
  | "private-marine";

export interface VhfChannel {
  /// Channel number as the radio displays it.
  number: number;
  /// Short purpose tag.
  purpose: string;
  /// One-line description of when to use it.
  use: string;
  tier: ChannelTier;
  /// True if dedicated to a single purpose (Ch 16, Ch 70). False if
  /// shared / commercial / general working.
  dedicated: boolean;
  /// Whether this is a UK-specific allocation (Ch 67 SAR, Ch 80 UK
  /// marina) or international.
  region: "international" | "uk";
}

export const VHF_CHANNELS: readonly VhfChannel[] = Object.freeze([
  {
    number: 16,
    purpose: "Distress, urgency, safety + calling",
    use: "Continuous watch by every vessel. Voice Mayday, Pan Pan and Securité are made on Ch 16. Initial inter-ship calling — then switch to a working channel.",
    tier: "distress",
    dedicated: true,
    region: "international",
  },
  {
    number: 70,
    purpose: "DSC — Digital Selective Calling",
    use: "Dedicated to digital distress + routine calling via the DSC controller. NO voice — voice on Ch 70 is a serious offence. Press the DSC distress button → it calls Ch 70 → then you go voice on Ch 16.",
    tier: "dsc",
    dedicated: true,
    region: "international",
  },
  {
    number: 13,
    purpose: "Bridge-to-bridge navigation safety",
    use: "Inter-ship safety + navigation conversations at low power (1 W) between vessels in sight. Used to coordinate course / speed / passing intentions in busy waters.",
    tier: "bridge-to-bridge",
    dedicated: true,
    region: "international",
  },
  {
    number: 6,
    purpose: "Inter-ship safety",
    use: "Inter-ship communications relating to safety. Used for SAR coordination too.",
    tier: "intership",
    dedicated: false,
    region: "international",
  },
  {
    number: 8,
    purpose: "Inter-ship",
    use: "General inter-ship working — switch here after an initial call on Ch 16.",
    tier: "intership",
    dedicated: false,
    region: "international",
  },
  {
    number: 67,
    purpose: "UK Coastguard SAR working",
    use: "UK Maritime Rescue Coordination Centres + RNLI lifeboats use this channel to coordinate a SAR operation after you've been picked up by Ch 16. Small Craft Safety scheme too.",
    tier: "coastguard-sar",
    dedicated: true,
    region: "uk",
  },
  {
    number: 80,
    purpose: "UK marina working",
    use: "UK marinas + harbour masters use Ch 80 for berth assignments + lock operation. Always check Ch 80 before entering a UK marina.",
    tier: "marina",
    dedicated: false,
    region: "uk",
  },
  {
    number: 23,
    purpose: "UK maritime safety broadcasts",
    use: "Maritime Safety Information (gale warnings, nav warnings, weather bulletins) broadcast by UK Coastguard at scheduled times.",
    tier: "weather",
    dedicated: false,
    region: "uk",
  },
  {
    number: 84,
    purpose: "UK MSI broadcasts (alternative)",
    use: "Alternative channel for UK Coastguard Maritime Safety Information broadcasts in some areas.",
    tier: "weather",
    dedicated: false,
    region: "uk",
  },
  {
    number: 86,
    purpose: "UK MSI broadcasts (alternative)",
    use: "Third alternative for UK MSI broadcasts; check the Coastguard's MSI schedule for the channel applicable to your area.",
    tier: "weather",
    dedicated: false,
    region: "uk",
  },
  {
    number: 10,
    purpose: "Pollution control + SAR coordination",
    use: "International channel reserved for pollution-incident coordination + on-scene SAR working.",
    tier: "coastguard-sar",
    dedicated: false,
    region: "international",
  },
  {
    number: 12,
    purpose: "Port operations",
    use: "Port operations + traffic-control channels (each port has its own — check the almanac or the chart's port-information block).",
    tier: "port-ops",
    dedicated: false,
    region: "international",
  },
  {
    number: 14,
    purpose: "Port operations",
    use: "Alternative port operations channel for ports where Ch 12 is busy.",
    tier: "port-ops",
    dedicated: false,
    region: "international",
  },
  {
    number: 9,
    purpose: "Marina (private), commercial",
    use: "Some marinas and commercial operators; used for tug + pilot coordination in some ports.",
    tier: "private-marine",
    dedicated: false,
    region: "international",
  },
  {
    number: 72,
    purpose: "Yacht safety + inter-ship",
    use: "Often used by rally/race organisers + cruising association inter-ship traffic.",
    tier: "intership",
    dedicated: false,
    region: "international",
  },
  {
    number: 77,
    purpose: "Yacht safety + inter-ship",
    use: "Common alternative inter-ship channel for casual yacht-to-yacht working.",
    tier: "intership",
    dedicated: false,
    region: "international",
  },
]);

/// All channels keyed by number for lookup convenience.
export const VHF_BY_NUMBER: Readonly<Record<number, VhfChannel>> =
  VHF_CHANNELS.reduce(
    (acc, ch) => {
      acc[ch.number] = ch;
      return acc;
    },
    {} as Record<number, VhfChannel>,
  );

// ───────────────────────────────────────────────────────────────────
// Call types
// ───────────────────────────────────────────────────────────────────

export type CallType = "mayday" | "panpan" | "securite" | "routine";

export interface VhfCallType {
  id: CallType;
  /// The procedure word as said on the air, repeated three times for
  /// distress / urgency / safety.
  procedureWord: string;
  /// French-origin spelling shown in the SRC syllabus.
  spelling: string;
  /// Plain-English title for the panel header.
  name: string;
  /// One-line summary of when to use it.
  summary: string;
  /// What "level" of trouble this signals.
  severity: "grave-imminent" | "urgent" | "important" | "normal";
  /// Step-by-step procedure for making the call. Order matters.
  steps: readonly string[];
  /// Real-world example a student can pattern-match.
  example: string;
  /// Common mistakes that get students into trouble.
  pitfalls: readonly string[];
}

export const VHF_CALL_TYPES: readonly VhfCallType[] = Object.freeze([
  {
    id: "mayday",
    procedureWord: "MAYDAY MAYDAY MAYDAY",
    spelling: "M'aidez (\"help me\") — French",
    name: "Distress (Mayday)",
    summary:
      "Vessel or person in GRAVE + IMMINENT danger requiring immediate assistance.",
    severity: "grave-imminent",
    steps: [
      "Press the DSC distress button first — opens the red flap, hold 5 seconds. The DSC controller broadcasts your MMSI + position on Ch 70.",
      "Switch to Ch 16 voice. The DSC handset usually does this automatically.",
      "Say: \"MAYDAY MAYDAY MAYDAY · This is [vessel name] [vessel name] [vessel name]\".",
      "Repeat: \"MAYDAY [vessel name]\" once more so the Coastguard hears your name clearly.",
      "Position: latitude/longitude or bearing + range from a known point.",
      "Nature of distress: sinking, fire, MOB, abandoning ship etc.",
      "Assistance required: evacuation, towing, helo lift, medical advice.",
      "Number of persons on board.",
      "Any other information (vessel description, EPIRB activated, etc.).",
      "End with \"OVER\". Release the PTT and listen for the Coastguard's response.",
    ],
    example:
      "MAYDAY MAYDAY MAYDAY · This is yacht Aurora yacht Aurora yacht Aurora · MAYDAY yacht Aurora · Position 50° 30.2'N 002° 15.8'W · We are sinking after a collision · We require immediate assistance · Three persons on board · Liferaft launched · OVER.",
    pitfalls: [
      "Forgetting the DSC button first — voice-only Mayday is slower + has less position accuracy.",
      "Calling Mayday for a non-life-threatening situation (use Pan Pan).",
      "Speaking too fast or panicking — slow down, read off your GPS, use the NATO phonetic for the vessel name.",
      "Not waiting for the Coastguard to respond before adding extra info — let them ask their questions.",
    ],
  },
  {
    id: "panpan",
    procedureWord: "PAN PAN PAN PAN PAN PAN",
    spelling: "Panne (\"breakdown\") — French",
    name: "Urgency (Pan Pan)",
    summary:
      "Urgent message concerning the safety of a vessel or person — but not in immediate danger of loss.",
    severity: "urgent",
    steps: [
      "Make a DSC urgency announcement (the DSC controller has a separate urgency mode — not all yacht radios have it; in that case go straight to voice).",
      "On Ch 16: \"PAN PAN PAN PAN PAN PAN\" three pairs.",
      "Address: \"All stations all stations all stations\" or to a specific station.",
      "Identify: \"This is [vessel name] [vessel name] [vessel name]\".",
      "Position.",
      "Nature of the urgency (engine failure, medical advice needed, lost crew member, taking on water but pumps coping).",
      "Action requested (tow assistance, medical advice, lookout request).",
      "Persons on board if relevant.",
      "End with \"OVER\".",
    ],
    example:
      "PAN PAN PAN PAN PAN PAN · All stations all stations all stations · This is yacht Aurora yacht Aurora yacht Aurora · Position 5 miles south of Anvil Point · Engine has failed and we are unable to steer clear of the shipping lane · Two persons on board · We require a tow · OVER.",
    pitfalls: [
      "Saying \"PAN PAN PAN\" once when you should say three pairs (\"PAN PAN PAN PAN PAN PAN\") — Coastguard may miss it.",
      "Using Mayday when Pan Pan is the right level — overuses the distress channel.",
      "Forgetting to say \"All stations\" if you don't know who can help — anybody listening may be the closest assistance.",
    ],
  },
  {
    id: "securite",
    procedureWord: "SECURITÉ SECURITÉ SECURITÉ",
    spelling: "Sécurité (\"safety\") — French, pronounced \"say-cure-it-eh\"",
    name: "Safety (Securité)",
    summary:
      "Important navigational or weather warning. Used by the Coastguard, ports, and occasionally yachts.",
    severity: "important",
    steps: [
      "On Ch 16: \"SECURITÉ SECURITÉ SECURITÉ\".",
      "Address: \"All stations all stations all stations\" (typical for a broadcast warning).",
      "Identify: \"This is [name] this is [name]\".",
      "Announce the channel the safety message will be passed on (e.g. \"Listen Ch 67\" or \"Listen Ch 23\").",
      "Switch to the working channel and broadcast the safety information.",
      "End with \"OUT\" (not \"Over\" — broadcast, no expected reply).",
    ],
    example:
      "SECURITÉ SECURITÉ SECURITÉ · All stations all stations all stations · This is Solent Coastguard this is Solent Coastguard · For navigational warning listen Ch 67 · OUT.",
    pitfalls: [
      "Yachts calling Securité for routine traffic — keep it for genuine safety information.",
      "Not switching to the announced working channel before broadcasting the detail.",
      "Pronunciation — it's the French word: \"say-cure-it-eh\", not \"securit-y\".",
    ],
  },
  {
    id: "routine",
    procedureWord: "(none — vessel name as call sign)",
    spelling: "Routine working — no preamble.",
    name: "Routine traffic",
    summary:
      "Normal calling + working — marina contact, fellow yachts, weather requests, port arrangements.",
    severity: "normal",
    steps: [
      "Listen on Ch 16 + the channel you're about to use to make sure they're clear.",
      "Call: \"[Other station] [other station] [other station] this is [your vessel] [your vessel] [your vessel] · OVER\".",
      "Other station responds with: \"[Your vessel] this is [other station] · go ahead · OVER\".",
      "Reply: \"This is [your vessel] · request you switch to Ch 80 · OVER\" (or whatever working channel).",
      "Other station confirms: \"This is [other station] · switching to Ch 80 · OVER\".",
      "Switch radio to Ch 80, re-establish: \"[Other station] this is [your vessel] on Ch 80 · OVER\".",
      "Pass message, end each transmission with \"OVER\" (\"OUT\" for the final one).",
    ],
    example:
      "Sparkes Marina Sparkes Marina Sparkes Marina · This is yacht Aurora yacht Aurora yacht Aurora · OVER. (Sparkes:) Aurora this is Sparkes Marina · go ahead · OVER. This is Aurora · request a berth for tonight · OVER.",
    pitfalls: [
      "Calling on Ch 16 then immediately passing traffic — switch to a working channel first.",
      "Forgetting to identify (\"This is yacht Aurora\") in the first transmission — Coastguard guidance and SRC syllabus both require it.",
      "Not closing with \"OUT\" — leaves the working channel ambiguously open.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// MMSI — Maritime Mobile Service Identity
// ───────────────────────────────────────────────────────────────────

export interface MmsiCountryCode {
  /// Three-digit Maritime Identification Digits (MID).
  mid: string;
  country: string;
}

/// A representative selection of MIDs the SRC syllabus covers. UK
/// + neighbours + commonly-met flag states. The full list (300+) is
/// in the ITU-R M.585 recommendation; this is enough to drill the
/// pattern.
export const MMSI_MIDS: readonly MmsiCountryCode[] = Object.freeze([
  { mid: "232", country: "UK" },
  { mid: "233", country: "UK" },
  { mid: "234", country: "UK" },
  { mid: "235", country: "UK" },
  { mid: "250", country: "Ireland" },
  { mid: "244", country: "Netherlands" },
  { mid: "245", country: "Netherlands" },
  { mid: "246", country: "Netherlands" },
  { mid: "227", country: "France" },
  { mid: "228", country: "France" },
  { mid: "211", country: "Germany" },
  { mid: "218", country: "Germany" },
  { mid: "224", country: "Spain" },
  { mid: "225", country: "Spain" },
  { mid: "247", country: "Italy" },
  { mid: "365", country: "USA (assigned for marine VHF DSC)" },
  { mid: "366", country: "USA" },
  { mid: "367", country: "USA" },
  { mid: "316", country: "Canada" },
  { mid: "311", country: "Bahamas" },
  { mid: "538", country: "Marshall Islands" },
  { mid: "563", country: "Singapore" },
  { mid: "503", country: "Australia" },
]);

export interface MmsiAnalysis {
  /// True if the input is a 9-digit string with no other characters.
  valid: boolean;
  /// Category of the MMSI — ship, group, coast station, SAR aircraft,
  /// or AIS-AtoN.
  category:
    | "ship"
    | "ship-group"
    | "coast-station"
    | "group-coast-station"
    | "sar-aircraft"
    | "ais-aton"
    | "auxiliary-craft"
    | "epirb"
    | "unknown";
  /// Three-digit MID extracted from the right place for this category.
  /// null if no MID is encoded in this number type.
  mid: string | null;
  /// Country lookup result.
  country: string | null;
  /// One-line plain-English explanation of what the number identifies.
  description: string;
}

// ───────────────────────────────────────────────────────────────────
// Radio licensing — Ship Radio Licence + SRC Operator's Authority
// ───────────────────────────────────────────────────────────────────

export interface RadioLicence {
  id: "ship-radio-licence" | "src-operator" | "lroc" | "gmdss-grc";
  /// Display name.
  name: string;
  /// One-line gist.
  summary: string;
  /// What it covers.
  covers: string;
  /// Who issues it.
  issuer: string;
  /// Validity period.
  validity: string;
  /// Cost (UK leisure).
  cost: string;
  /// Application route.
  application: string;
}

export const RADIO_LICENCES: readonly RadioLicence[] = Object.freeze([
  {
    id: "ship-radio-licence",
    name: "Ship Radio Licence",
    summary:
      "The licence that authorises a BOAT to carry + use VHF radio equipment.",
    covers:
      "Use of VHF + DSC + EPIRB + radar transponders + AIS on the named vessel. NOT person-specific — applies to the boat.",
    issuer: "Ofcom (UK).",
    validity:
      "Free + lifetime (since April 2020 in the UK). Was an annual fee before.",
    cost: "Free in the UK (since April 2020).",
    application:
      "Apply at the Ofcom website. Need: boat details (name, owner, MMSI if existing, year + length). Receive a callsign + an MMSI (if you didn't have one).",
  },
  {
    id: "src-operator",
    name: "SRC — Short Range Certificate (Operator's Authority)",
    summary:
      "The licence that authorises a PERSON to operate VHF + DSC equipment on a vessel.",
    covers:
      "Operation of fixed + handheld marine VHF, including DSC + EPIRB activation. Person-specific.",
    issuer: "RYA (training + examination); Ofcom (validation).",
    validity: "Lifetime once obtained.",
    cost:
      "RYA SRC course typically £100-150 (online or classroom) + exam fee.",
    application:
      "Take an RYA-recognised SRC course covering operating procedures, DSC, EPIRB, NAVTEX, IMO regs. Sit + pass the practical + theory exam. Apply to Ofcom (free) for the operator's authority based on the SRC certificate.",
  },
  {
    id: "lroc",
    name: "LRC — Long Range Certificate",
    summary:
      "The licence for HF/SSB + INMARSAT operation. Needed for ocean cruising.",
    covers:
      "All SRC subjects PLUS HF/SSB voice + email, INMARSAT satellite comms, NAVTEX detail, deep-sea distress procedures.",
    issuer: "RYA (training); Ofcom (validation).",
    validity: "Lifetime.",
    cost: "RYA LRC course £200-400.",
    application:
      "After SRC + experience, take an LRC course. Required for ocean racing + bluewater cruising.",
  },
  {
    id: "gmdss-grc",
    name: "GMDSS GRC — General Radio Operator's Certificate",
    summary:
      "Professional-grade qualification for officers on SOLAS-certified commercial vessels.",
    covers:
      "Full GMDSS (Global Maritime Distress + Safety System) operational competence. Far beyond leisure-sailor requirements.",
    issuer: "MCA + recognised training colleges.",
    validity: "5 years (revalidation required).",
    cost: "Several thousand pounds — full college course.",
    application:
      "Required for commercial vessel officers. Not relevant to recreational sailors.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// RT log — what to write down + retention
// ───────────────────────────────────────────────────────────────────

export interface RtLogColumn {
  /// Column heading.
  name: string;
  /// What goes in this column.
  contents: string;
  /// Example entry.
  example: string;
}

export const RT_LOG_COLUMNS: readonly RtLogColumn[] = Object.freeze([
  {
    name: "Time",
    contents: "Time of the radio communication, in UT or local — be consistent throughout the log.",
    example: "1430 (UT)",
  },
  {
    name: "Channel",
    contents: "VHF channel used — typically 16 for first contact, then the working channel.",
    example: "16, then 80",
  },
  {
    name: "Direction",
    contents: "FROM / TO — who initiated + who responded. Useful for tracing later.",
    example: "FROM: Aurora; TO: Sparkes Marina",
  },
  {
    name: "Content / message",
    contents: "Brief summary of the message — not verbatim. Names + key info recorded.",
    example: "Requested berth for tonight; confirmed E2 berth available; ETA 1530",
  },
  {
    name: "Outcome / action",
    contents: "What was agreed + any follow-up needed.",
    example: "Berth E2 confirmed; proceeded toward marina; arrived 1545",
  },
]);

export const RT_LOG_RETENTION: readonly string[] = Object.freeze([
  "Keep the RT log for the duration of the voyage + 12 months after.",
  "DISTRESS or URGENCY traffic: log MUST be retained for at least 2 years.",
  "The log is admissible in court for an incident investigation — write clearly + factually.",
  "Don't editorialise — record what was said + what was done, not what you thought.",
  "Time + position at the start of each watch logged.",
  "Routine traffic + standard exchanges can be brief; complex situations get detailed entries.",
  "If a Mayday or Pan Pan was made (sent or received), log the full text + the response received.",
]);

/// Decode an MMSI into its category + MID + country. Recognises the
/// common SRC-syllabus forms — ship (MIDxxxxxx), coast station
/// (00MIDxxxx), SAR aircraft (111MIDxxx), ship group (0MIDxxxxx),
/// AIS-AtoN (99MIDxxxx), auxiliary craft (98MIDxxxx), EPIRB (974xxxxxx).
export function analyseMmsi(input: string): MmsiAnalysis {
  const digits = input.replace(/\s/g, "");
  if (!/^\d{9}$/.test(digits)) {
    return {
      valid: false,
      category: "unknown",
      mid: null,
      country: null,
      description: "Not a valid 9-digit MMSI.",
    };
  }

  // Patterns are matched in the order the ITU defines them.
  let category: MmsiAnalysis["category"];
  let mid: string | null = null;
  let description: string;

  if (digits.startsWith("00")) {
    category = "coast-station";
    mid = digits.substring(2, 5);
    description = `Coast (shore) station — typically a Coastguard MRCC or a port station. MID = ${mid}.`;
  } else if (digits.startsWith("0")) {
    category = "ship-group";
    mid = digits.substring(1, 4);
    description = `Ship group — multiple vessels can be addressed together (rally, racing fleet, search team). MID = ${mid}.`;
  } else if (digits.startsWith("111")) {
    category = "sar-aircraft";
    mid = digits.substring(3, 6);
    description = `SAR aircraft (helicopter / fixed-wing). MID = ${mid}.`;
  } else if (digits.startsWith("99")) {
    category = "ais-aton";
    mid = digits.substring(2, 5);
    description = `AIS Aid-to-Navigation (a buoy or beacon transmitting AIS). MID = ${mid}.`;
  } else if (digits.startsWith("98")) {
    category = "auxiliary-craft";
    mid = digits.substring(2, 5);
    description = `Auxiliary craft associated with a parent vessel (tender, RIB). MID = ${mid}.`;
  } else if (digits.startsWith("974")) {
    category = "epirb";
    mid = null;
    description =
      "AIS-equipped EPIRB / PLB / MOB beacon. The 974 prefix marks it as a personal locating device, not a vessel.";
  } else {
    category = "ship";
    mid = digits.substring(0, 3);
    description = `Ship station — a single vessel. MID = ${mid}.`;
  }

  const country =
    mid !== null
      ? (MMSI_MIDS.find((m) => m.mid === mid)?.country ?? "Unrecognised MID")
      : null;

  return {
    valid: true,
    category,
    mid,
    country,
    description,
  };
}
